import React, { useState, useEffect, useRef } from "react";
import { X, Info, Layers, Hammer, MessageSquare, ChevronDown, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const ITEM_TYPE_CHOICES = [
  { value: "Table", label: "Table" },
  { value: "Chair", label: "Chair" },
  { value: "Whiteboard", label: "Whiteboard" },
  { value: "Desk", label: "Teacher Desk" },
  { value: "Other", label: "Other Asset" },
];

export default function ClassroomInventoryModal({
  isOpen,
  onClose,
  onSave,
  inventoryItem, // Passes existing object when editing, null when creating
  formData,
  setFormData,
}) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close custom dropdown when clicking outside (Standard 21st.dev utility behavior)
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  // 1. Safe computation using optional chaining
  const computedTotal =
    (Number(formData?.quantity_functional) || 0) +
    (Number(formData?.quantity_damaged) || 0);

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 overflow-y-auto">
          {/* Ambient Backdrop Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-slate-950/40 backdrop-blur-[6px]"
          />

          {/* Premium Floating Structural Surface Card */}
          <motion.div
            initial={{ scale: 0.98, opacity: 0, y: 8 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.98, opacity: 0, y: 8 }}
            transition={{ type: "spring", duration: 0.3, bounce: 0 }}
            className="bg-white rounded-xl w-full max-w-md shadow-2xl border border-slate-100 relative z-10 my-auto flex flex-col overflow-visible"
          >
            <div className="absolute top-0 left-6 right-6 h-[2px] bg-gradient-to-r from-transparent via-[#002855]/30 to-transparent" />

            {/* Modal Header Module */}
            <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50 rounded-t-2xl">
              <div>
                <h2 className="text-sm font-semibold text-slate-900 tracking-tight flex items-center gap-2">
                  <Layers size={16} className="text-[#002855]" />
                  {inventoryItem ? "Modify Inventory Metrics" : "Log New Classroom Asset"}
                </h2>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Track physical furniture components and operational health logs.
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-200/50 transition-colors cursor-pointer"
              >
                <X size={15} />
              </button>
            </div>

            {/* Modal Body Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-4 text-xs font-medium text-slate-600">
              
              {/* Custom Item Type Dropdown Select Module */}
              <div className="space-y-1.5 relative" ref={dropdownRef}>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                  Item Type Classification <span className="text-rose-500">*</span>
                </label>
                
                <button
                  type="button"
                  disabled={!!inventoryItem} // Unique constraint block: don't let types change during updates
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className={`w-full px-3 py-2 bg-slate-50/50 border border-slate-200 rounded-xl font-medium text-slate-800 outline-none flex items-center justify-between transition-all ${
                    inventoryItem 
                      ? "opacity-60 cursor-not-allowed bg-slate-100" 
                      : "focus:ring-2 focus:ring-[#002855]/10 focus:border-[#002855] focus:bg-white cursor-pointer"
                  }`}
                >
                  <span>
                    {ITEM_TYPE_CHOICES.find((c) => c.value === formData?.item_type)?.label || "Select item configuration..."}
                  </span>
                  <ChevronDown size={14} className={`text-slate-400 transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`} />
                </button>

                {/* Dropdown Options List Menu */}
                <AnimatePresence>
                  {dropdownOpen && !inventoryItem && (
                    <motion.div
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      transition={{ duration: 0.15 }}
                      className="absolute z-50 w-full mt-1 bg-white border border-slate-100 shadow-xl rounded-xl overflow-hidden py-1"
                    >
                      {ITEM_TYPE_CHOICES.map((choice) => (
                        <button
                          key={choice.value}
                          type="button"
                          onClick={() => {
                            setFormData({ ...formData, item_type: choice.value });
                            setDropdownOpen(false);
                          }}
                          className="w-full px-3 py-2 text-left hover:bg-slate-50 font-medium text-slate-700 flex items-center justify-between transition-colors cursor-pointer"
                        >
                          <span>{choice.label}</span>
                          {formData.item_type === choice.value && (
                            <Check size={12} className="text-[#002855]" />
                          )}
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* 1x2 Quantities Grid Split */}
              <div className="grid grid-cols-2 gap-3">
                
                {/* Functional Items Count */}
                <div className="space-y-1.5">
                  <label className="text-[10px] font-semibold uppercase tracking-wider text-emerald-600 flex items-center gap-1">
                    <Layers size={12} className="text-emerald-500" />
                    Functional Units
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.quantity_functional ?? 0}
                    onChange={(e) => setFormData({ ...formData, quantity_functional: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-1.5 bg-slate-50/50 border border-slate-200 rounded-xl font-medium text-slate-800 outline-none focus:ring-2 focus:ring-emerald-500/10 focus:border-emerald-500 focus:bg-white transition-all"
                    required
                  />
                </div>

                {/* Damaged / Broken Items Count */}
                <div className="space-y-1.5">
                  <label className="text-[10px] font-semibold uppercase tracking-wider text-amber-600 flex items-center gap-1">
                    <Hammer size={12} className="text-amber-500" />
                    Damaged Units
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.quantity_damaged ?? 0}
                    onChange={(e) => setFormData({ ...formData, quantity_damaged: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-1.5 bg-slate-50/50 border border-slate-200 rounded-xl font-medium text-slate-800 outline-none focus:ring-2 focus:ring-amber-500/10 focus:border-amber-500 focus:bg-white transition-all"
                    required
                  />
                </div>

              </div>

              {/* Remarks logs Context Area */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                  <MessageSquare size={12} className="text-slate-400" />
                  Condition Discrepancy & Repair Notes
                </label>
                <textarea
                  placeholder="Describe specific defects, missing components, or outstanding work orders..."
                  value={formData.remarks || ""}
                  onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 bg-slate-50/50 border border-slate-200 rounded-xl font-medium text-slate-800 placeholder-slate-400 outline-none focus:ring-2 focus:ring-[#002855]/10 focus:border-[#002855] focus:bg-white transition-all resize-none"
                />
              </div>

              {/* Modal Footer / Action Block Controls */}
              <div className="flex items-center justify-between pt-3 border-t border-slate-100 mt-5">
                <div className="flex items-center gap-1.5 text-[10px] font-mono font-bold text-slate-400 uppercase tracking-wider">
                  <Info size={12} className="text-slate-300 shrink-0" />
                  Total Items: <span className="text-[#002855] bg-[#002855]/5 border border-[#002855]/10 px-1.5 py-0.5 rounded-md ml-1">{computedTotal} UNITS</span>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100 bg-white border border-slate-200 rounded-xl transition-all cursor-pointer"
                  >
                    Discard
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-xs font-medium text-white bg-[#002855] bg-opacity-80 hover:bg-opacity-95 rounded-xl shadow-xs transition-all cursor-pointer active:scale-98"
                  >
                    {inventoryItem ? "Save Changes" : "Commit Record"}
                  </button>
                </div>
              </div>

            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}